// Enhanced offline data management for AgroSense
interface OfflineFarm {
  id: string;
  name: string;
  location: string;
  size: number;
  crops: string[];
  userId: string;
  createdAt: string;
  lastSync?: string;
}

interface OfflineTask {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  dueDate: string;
  farmId: string;
  userId: string;
  createdAt: string;
  lastSync?: string;
}

interface OfflineSoilReading {
  id: string;
  farmId: string;
  nitrogen: number;
  phosphorus: number;
  potassium: number;
  ph: number;
  moisture: number;
  timestamp: string;
  userId: string;
  lastSync?: string;
}

class OfflineDataManager {
  private storagePrefix = 'agrosense_offline_';

  // Generate offline ID
  private generateId(): string {
    return 'offline_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Generic storage methods
  private getStorageKey(type: string, userId: string): string {
    return `${this.storagePrefix}${type}_${userId}`;
  }

  private getData<T>(type: string, userId: string): T[] {
    try {
      const data = localStorage.getItem(this.getStorageKey(type, userId));
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  private setData<T>(type: string, userId: string, data: T[]): void {
    localStorage.setItem(this.getStorageKey(type, userId), JSON.stringify(data));
  }

  // Farm management
  getFarms(userId: string): OfflineFarm[] {
    return this.getData<OfflineFarm>('farms', userId);
  }

  addFarm(userId: string, farm: Omit<OfflineFarm, 'id' | 'userId' | 'createdAt'>): OfflineFarm {
    const farms = this.getFarms(userId);
    const newFarm: OfflineFarm = {
      ...farm,
      id: this.generateId(),
      userId,
      createdAt: new Date().toISOString()
    };
    
    farms.push(newFarm);
    this.setData('farms', userId, farms);
    return newFarm;
  }

  updateFarm(userId: string, farmId: string, updates: Partial<OfflineFarm>): OfflineFarm | null {
    const farms = this.getFarms(userId);
    const index = farms.findIndex(f => f.id === farmId);
    
    if (index === -1) return null;
    
    farms[index] = { ...farms[index], ...updates };
    this.setData('farms', userId, farms);
    return farms[index];
  }

  deleteFarm(userId: string, farmId: string): boolean {
    const farms = this.getFarms(userId);
    const filtered = farms.filter(f => f.id !== farmId);
    
    if (filtered.length === farms.length) return false;
    
    this.setData('farms', userId, filtered);
    // Also delete related tasks and soil readings
    this.deleteTasksByFarm(userId, farmId);
    this.deleteSoilReadingsByFarm(userId, farmId);
    return true;
  }

  // Task management
  getTasks(userId: string, farmId?: string): OfflineTask[] {
    const tasks = this.getData<OfflineTask>('tasks', userId);
    return farmId ? tasks.filter(t => t.farmId === farmId) : tasks;
  }

  addTask(userId: string, task: Omit<OfflineTask, 'id' | 'userId' | 'createdAt'>): OfflineTask {
    const tasks = this.getTasks(userId);
    const newTask: OfflineTask = {
      ...task,
      id: this.generateId(),
      userId,
      createdAt: new Date().toISOString()
    };
    
    tasks.push(newTask);
    this.setData('tasks', userId, tasks);
    return newTask;
  }

  updateTask(userId: string, taskId: string, updates: Partial<OfflineTask>): OfflineTask | null {
    const tasks = this.getTasks(userId);
    const index = tasks.findIndex(t => t.id === taskId);
    
    if (index === -1) return null;
    
    tasks[index] = { ...tasks[index], ...updates };
    this.setData('tasks', userId, tasks);
    return tasks[index];
  }

  deleteTask(userId: string, taskId: string): boolean {
    const tasks = this.getTasks(userId);
    const filtered = tasks.filter(t => t.id !== taskId);
    
    if (filtered.length === tasks.length) return false;
    
    this.setData('tasks', userId, filtered);
    return true;
  }

  private deleteTasksByFarm(userId: string, farmId: string): void {
    const tasks = this.getTasks(userId);
    const filtered = tasks.filter(t => t.farmId !== farmId);
    this.setData('tasks', userId, filtered);
  }

  // Soil readings management
  getSoilReadings(userId: string, farmId?: string): OfflineSoilReading[] {
    const readings = this.getData<OfflineSoilReading>('soil_readings', userId);
    return farmId ? readings.filter(r => r.farmId === farmId) : readings;
  }

  addSoilReading(userId: string, reading: Omit<OfflineSoilReading, 'id' | 'userId'>): OfflineSoilReading {
    const readings = this.getSoilReadings(userId);
    const newReading: OfflineSoilReading = {
      ...reading,
      id: this.generateId(),
      userId
    };
    
    readings.push(newReading);
    this.setData('soil_readings', userId, readings);
    return newReading;
  }

  getLatestSoilReading(userId: string, farmId: string): OfflineSoilReading | null {
    const readings = this.getSoilReadings(userId, farmId);
    if (readings.length === 0) return null;
    
    return readings.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];
  }

  private deleteSoilReadingsByFarm(userId: string, farmId: string): void {
    const readings = this.getSoilReadings(userId);
    const filtered = readings.filter(r => r.farmId !== farmId);
    this.setData('soil_readings', userId, filtered);
  }

  // Generate sample data for demo
  generateSampleData(userId: string): void {
    // Only generate if no data exists
    if (this.getFarms(userId).length > 0) return;

    // Sample farms
    const sampleFarms = [
      {
        name: 'North Field',
        location: 'Maseru District',
        size: 2.5,
        crops: ['Maize', 'Beans']
      },
      {
        name: 'South Valley',
        location: 'Berea District', 
        size: 1.8,
        crops: ['Wheat', 'Sorghum']
      }
    ];

    const farms = sampleFarms.map(farm => this.addFarm(userId, farm));

    // Sample tasks
    farms.forEach(farm => {
      this.addTask(userId, {
        title: 'Check irrigation system',
        description: 'Inspect and maintain irrigation equipment',
        completed: false,
        dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        farmId: farm.id
      });

      this.addTask(userId, {
        title: 'Apply fertilizer',
        description: 'Apply NPK fertilizer to crops',
        completed: true,
        dueDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        farmId: farm.id
      });

      // Sample soil reading
      this.addSoilReading(userId, {
        farmId: farm.id,
        nitrogen: Math.round(15 + Math.random() * 20),
        phosphorus: Math.round(15 + Math.random() * 20),
        potassium: Math.round(15 + Math.random() * 20),
        ph: Math.round((6 + Math.random() * 2) * 10) / 10,
        moisture: Math.round(30 + Math.random() * 40),
        timestamp: new Date().toISOString()
      });
    });
  }

  // Clear all data for user
  clearUserData(userId: string): void {
    ['farms', 'tasks', 'soil_readings'].forEach(type => {
      localStorage.removeItem(this.getStorageKey(type, userId));
    });
  }

  // Get sync status
  getPendingSyncItems(userId: string): {
    farms: OfflineFarm[];
    tasks: OfflineTask[];
    soilReadings: OfflineSoilReading[];
  } {
    return {
      farms: this.getFarms(userId).filter(f => !f.lastSync),
      tasks: this.getTasks(userId).filter(t => !t.lastSync),
      soilReadings: this.getSoilReadings(userId).filter(r => !r.lastSync)
    };
  }
}

export const offlineData = new OfflineDataManager();
export type { OfflineFarm, OfflineTask, OfflineSoilReading };